/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';

/********************************************* EXPORT *********************************************/
/**
 * Handles clicks outside the given children
 *
 * Example
 *     Closes modal if user clicks outside it - modal is the containing
 *     component's children:
 *         <ClickOutside onClickOutside={_ev => {
 *              closeModal()
 *         }}>
 *             {children}
 *         </ClickOutside>
 */
export class ClickOutside extends React.Component<{onClickOutside: EventHandler}> {
    private isTouch = false;
    private container: HTMLDivElement = null;

    /**
     * Store ref to the element on render
     * Wrap children
     */
    render() {
        const {children, onClickOutside, ...props} = this.props;
        return (
            <div {...props} ref={this.getContainer}>
                {children}
            </div>
        );
    }

    /**
     * Register 'global' click/touch listeners directly after component renders
     */
    componentDidMount() {
        document.addEventListener(`touchend`, this.handleOutsideClick, {passive: false});
        document.addEventListener(`click`, this.handleOutsideClick, true);
    }

    /**
     * Unregister global click/touch listeners when component is being removed
     */
    componentWillUnmount() {
        document.removeEventListener(`touchend`, this.handleOutsideClick, true);
        document.removeEventListener(`click`, this.handleOutsideClick, true);
    }

    /**
     * Get reference to the HTML element holding the components
     */
    private getContainer = (ref: HTMLDivElement) => (this.container = ref);

    /**
     * Triggered on click anywhere onscreen, but if click is outside children
     * DOM elements, runs onClickOutside callback prop with the click event
     */
    private handleOutsideClick = (event: TouchEvent | MouseEvent) => {
        if (event.type === `touchend`) this.isTouch = true;
        if (event.type === `click` && this.isTouch) return;
        const {onClickOutside} = this.props;
        const el = this.container;
        if (!el.contains(event.target as any)) onClickOutside(event);
    };
}
